import { useEffect, useState } from "react";
import {
    Database, BookOpen, GraduationCap, School, Globe2, MapPin, Building2,
    Mailbox, Languages, Award, FileText, Layers, Trash2, FileClock,
} from "lucide-react";
import { api } from "../../../lib/api";

const KIND_META = {
    subject: { label: "Subjects", icon: BookOpen, color: "bg-blue-50 text-blue-700 border-blue-200" },
    grade: { label: "Grades", icon: GraduationCap, color: "bg-purple-50 text-purple-700 border-purple-200" },
    board: { label: "Boards", icon: School, color: "bg-amber-50 text-amber-800 border-amber-200" },
    country: { label: "Countries", icon: Globe2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    state: { label: "States", icon: MapPin, color: "bg-rose-50 text-rose-700 border-rose-200" },
    city: { label: "Cities", icon: Building2, color: "bg-orange-50 text-orange-700 border-orange-200" },
    pincode: { label: "Pincodes", icon: Mailbox, color: "bg-slate-50 text-slate-700 border-slate-300" },
    language: { label: "Languages", icon: Languages, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
    exam_category: { label: "Exam Categories", icon: Award, color: "bg-pink-50 text-pink-700 border-pink-200" },
    curriculum_type: { label: "Curriculum Types", icon: FileText, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    curriculum_pack: { label: "Curriculum Packs", icon: Layers, color: "bg-violet-50 text-violet-700 border-violet-200" },
};

export default function MasterDataDashboard({ onJump }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/master-ops/dashboard/stats")
            .then((r) => setStats(r.data))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-6 text-sm text-slate-500">Loading dashboard…</div>;
    if (!stats) return <div className="p-6 text-sm text-red-600">Failed to load stats.</div>;

    const ordered = ["country", "curriculum_pack", "curriculum_type", "board", "grade", "subject",
        "language", "exam_category", "state", "city", "pincode"];

    return (
        <div className="space-y-4" data-testid="master-data-dashboard">
            {/* Headline stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <HeadlineCard label="Active Mappings" value={stats.totals?.active_mappings || 0} icon={Layers} color="bg-violet-100 text-violet-800" />
                <HeadlineCard label="Audit Entries" value={stats.totals?.audit_entries || 0} icon={FileClock} color="bg-blue-100 text-blue-800" />
                <HeadlineCard label="Recycle Bin" value={stats.totals?.recycled || 0} icon={Trash2} color="bg-rose-100 text-rose-800" />
                <HeadlineCard label="Total Kinds" value={Object.keys(stats.by_kind || {}).length} icon={Database} color="bg-emerald-100 text-emerald-800" />
            </div>

            {/* Per-kind grid */}
            <div>
                <h3 className="text-sm font-bold text-slate-600 mb-2 uppercase tracking-wider">Master Data Tables</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {ordered.map((k) => {
                        const meta = KIND_META[k];
                        const s = stats.by_kind?.[k];
                        if (!meta || !s) return null;
                        const Icon = meta.icon;
                        return (
                            <button
                                key={k}
                                onClick={() => onJump?.(k)}
                                data-testid={`md-dash-card-${k}`}
                                className={`text-left p-4 rounded-2xl border ${meta.color} hover:shadow-md transition-all`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className="w-4 h-4" />
                                    <span className="text-xs font-bold uppercase tracking-wider">{meta.label}</span>
                                </div>
                                <div className="text-2xl font-bold">{s.total}</div>
                                <div className="text-[11px] mt-1 opacity-75">
                                    <span className="font-semibold">{s.active}</span> active
                                    {s.drafts > 0 && <> · <span className="font-semibold">{s.drafts}</span> drafts</>}
                                    {s.recycled > 0 && <> · <span className="font-semibold">{s.recycled}</span> recycled</>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function HeadlineCard({ label, value, icon: Icon, color }) {
    return (
        <div className={`p-4 rounded-2xl ${color}`} data-testid={`md-headline-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-wider opacity-75">{label}</div>
                <Icon className="w-4 h-4 opacity-50" />
            </div>
            <div className="text-3xl font-bold mt-2">{value}</div>
        </div>
    );
}
